import { useRef, useState } from "react";
import { Container } from "reactstrap";

import useUserAccounts from "../../../api/api-hooks/auth/useUserAccounts";
import ChangePasswordDrawer from "./ChangePasswordDrawer";
import AccountSessions from "./AccountSessions";
import ListItem from "../../../components/CRM/ListItem";
import UserCredentialOptions from "../../../components/Menus/UserCredentialOptions";
import AccountSessionHistory from "./AccountSessionHistory";
import useDeleteAccountCredential from "../../../api/api-hooks/auth/useDeleteAccountCredential";
import { AppConfirm } from "../../../utils/PopupUtil";
import { Providers, Credential } from "../../../types/GlobalInterfaces";

const getProviderLogo = (provider: Providers) => {
  switch (provider) {
    case "local":
      return "GrowthCloud.com Account";
    case "office365":
      return "Microsoft Office365 Single Sign-On";
    case "amazon":
      return "Amazon Single Sign-On";
    case "google":
      return "Google Single Sign-On";
    case "linkedin":
      return "LinkedIn Single Sign-On";
    default:
      return "";
  }
};

const getProviderSymbol = (provider: Providers) => {
  switch (provider) {
    case "local":
      return "mdi-lock-outline";
    case "office365":
      return "mdi-microsoft-office";
    case "amazon":
      return "mdi mdi-aws";
    case "google":
      return "mdi mdi-google";
    case "linkedin":
      return "mdi mdi-linkedin";
    default:
      return "mdi mdi-lock-outline";
  }
};

const AccountSecurity = () => {
  const { userAccounts, userAccountsLoading } = useUserAccounts();
  const { deleteAccountCredential, deletingCredential } =
    useDeleteAccountCredential();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const credentialOptionsRef = useRef<any>();

  return (
    <>
      <Container fluid>
        <div className="px-3">
          <h3 className="mb-0">Credentials</h3>
          <div>
            <small>
              This is a list of the login credentials that can be used to access
              your GrowthCloud account.
            </small>
          </div>
          <hr className="mt-2 mb-3"></hr>
          {userAccountsLoading ? (
            <>
              <ListItem loading={true} />
              <ListItem loading={true} />
            </>
          ) : (
            <>
              {userAccounts?.map((account) => (
                <ListItem
                  key={account.id}
                  title={`${account?.Email}`}
                  subTitle={getProviderLogo(account.Provider)}
                  icon={`mdi ${getProviderSymbol(account.Provider)}`}
                  options={
                    <UserCredentialOptions
                      credential={account}
                      changePassword={(cred: any) => {
                        setSelectedAccount(cred);
                      }}
                      removeCredentialDisabled={userAccounts?.length === 1}
                      removeCredential={
                        deletingCredential ? () => {} : handleRemoveCredential
                      }
                      closeWhisper={() => credentialOptionsRef.current?.close()}
                    />
                  }
                />
              ))}
            </>
          )}
          {/* Disabled MFA action until the feature is fully implemented (Frontend changes are needed too)  */}
          <div className="mt-5">{/* <MFA /> */}</div>
          <div className="mt-5">
            <AccountSessions />
          </div>
          <div className="mt-5 mb-5">
            <AccountSessionHistory />
          </div>
        </div>
      </Container>

      <ChangePasswordDrawer
        account={selectedAccount}
        open={selectedAccount !== null}
        onClose={() => setSelectedAccount(null)}
      />
    </>
  );

  async function handleRemoveCredential(credential: Credential) {
    const result = await AppConfirm({
      title: "Are you sure you want to remove this credential?",
      // @ts-ignore
      text: "You will no longer be able to login using this credential.",
      confirmButtonText: "Remove",
    });

    if (!result.isConfirmed) return;

    deleteAccountCredential({ accountID: credential._id! });
  }
};

export default AccountSecurity;
